import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";


export const Route = createFileRoute("/prijava")({
  head: () => {
    const url = "https://kupish.si/prijava";
    const title = "Prijava — kupish.si";
    const description = "Prijavite se v svoj račun na kupish.si za pregled naročil, priljubljenih izdelkov in upravljanje svoje spletne trgovine.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: Page,
});

function Page() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  useEffect(() => {
    if (ready && user) navigate({ to: "/dashboard", replace: true });
  }, [ready, user, navigate]);

  if (!ready) {
    return (
      <section className="mx-auto max-w-md px-6 py-24 text-muted-foreground">
        Nalagam…
      </section>
    );
  }

  function persistSession() {
    try {
      window.localStorage.setItem("kupish:remember-me", "true");
      window.sessionStorage.removeItem("kupish:session-active");
    } catch {}
  }

  async function onMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLinkSent(false);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Vnesite veljaven e-naslov.");
      return;
    }
    setLinkLoading(true);
    persistSession();
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/potrdi-email`,
      },
    });
    setLinkLoading(false);
    if (error) setError(error.message);
    else setLinkSent(true);
  }

  return (
    <section className="mx-auto max-w-md px-6 py-24">
      <div className="eyebrow">Dobrodošli</div>
      <h1 className="font-serif text-5xl mt-3">Prijava</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Vnesite svoj e-naslov in poslali vam bomo povezavo za prijavo. S klikom na
        povezavo se prijavite — gesla ne potrebujete. Po potrditvi ostanete prijavljeni
        v tem brskalniku; ko prijava poteče, si preprosto znova pošljete povezavo.
      </p>

      <form className="mt-8 border border-border p-5" onSubmit={onMagicLink}>
        <label className="text-xs eyebrow" htmlFor="magic-email">E-naslov</label>
        <input
          id="magic-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
          placeholder="vi@gmail.com"
        />
        <button
          type="submit"
          disabled={linkLoading}
          className="mt-3 w-full bg-primary text-primary-foreground py-3 text-sm hover:opacity-90 disabled:opacity-60"
        >
          {linkLoading ? "Pošiljam povezavo…" : "Pošlji mi povezavo za prijavo"}
        </button>
        {error && <div className="mt-3 text-sm text-destructive">{error}</div>}
        {linkSent && (
          <p className="mt-3 text-sm text-foreground">
            Povezavo smo poslali na <strong>{email}</strong>. Odprite e-pošto in kliknite
            povezavo za prijavo.
          </p>
        )}
      </form>

      <div className="mt-6 text-sm text-muted-foreground text-center">
        Še nimate računa? <Link to="/registracija" className="underline underline-offset-4 hover:text-accent">Registrirajte se</Link>
      </div>
    </section>
  );
}

